import { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { ErrorFallbackScreen } from './ErrorFallbackScreen';

interface ErrorBoundaryProps {
  children: ReactNode;
  variant?: 'fullscreen' | 'inline';
}

interface ErrorBoundaryState {
  hasError: boolean;
  isChunkError: boolean;
}

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('Unable to preload CSS')
  );
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, { componentStack }: React.ErrorInfo) {
    Sentry.captureException(error, { contexts: { react: { componentStack } } });
  }

  reset = () => {
    if (this.state.isChunkError) {
      // Stale cached chunk — hard reload so the SW serves fresh assets
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, isChunkError: false });
  };

  render() {
    const { children, variant = 'fullscreen' } = this.props;
    if (this.state.hasError) {
      return <ErrorFallbackScreen onReset={this.reset} variant={variant} />;
    }
    return children;
  }
}

export function PageErrorBoundary({ children }: { children: ReactNode }) {
  return <ErrorBoundary variant="inline">{children}</ErrorBoundary>;
}
